import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertCircle, LayoutPanelLeft } from 'lucide-react';
import { TemplateGalleryProps, TemplateCatalog } from '../../types/template';
import { TemplateCard } from './TemplateCard';
import { EmptyState } from '@/components/ui/empty-state';
import { Paginator } from '@/components/ui/paginator';
import { api } from '@/api/sdk';
import {
  buildListQuery,
  type Paginated,
  type PaginationMeta,
} from '@/api/pagination';

interface ExtendedTemplateGalleryProps extends TemplateGalleryProps {
  /** Search query for fuzzy search (optional) */
  search?: string;
  selectedTemplateId?: string;
  /** Whether filters are currently applied (affects empty state messaging) */
  hasFilters?: boolean;
}

/**
 * TemplateGallery Component
 *
 * Main template browsing interface for selecting templates
 */
export const TemplateGallery: React.FC<ExtendedTemplateGalleryProps> = ({
  onTemplateSelect,
  onTemplatePreview,
  organizationId: _organizationId,
  search,
  selectedTemplateId,
  hasFilters = false,
}) => {
  const [templates, setTemplates] = useState<TemplateCatalog[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset to page 1 when the search term changes so we don't land on an
  // empty page mid-paginate.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = `/api/templates${buildListQuery(
        { page },
        { search: search?.trim() || undefined },
      )}`;
      const result = await api.get<Paginated<TemplateCatalog>>(endpoint);
      setTemplates(result.items);
      setPagination(result.pagination);
    } catch (err: any) {
      console.error('Failed to load templates:', err);
      const errorMessage = err.message || 'Failed to load templates';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // All templates sorted by name
  const displayedTemplates = useMemo(() => {
    return [...templates].sort((a, b) => {
      return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase());
    });
  }, [templates]);

  const handleTemplateClick = (template: TemplateCatalog) => {
    onTemplateSelect(template);
  };

  if (loading) {
    return (
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1"
        aria-busy="true"
        aria-live="polite"
      >
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="flex flex-col rounded-2xl overflow-hidden animate-pulse"
            style={{ animationDelay: `${i * 120}ms` }}
          >
            <div className="aspect-video bg-foreground/5" />
            <div className="px-4 py-3 bg-foreground">
              <div className="h-5 w-1/2 rounded bg-background/15" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Couldn't load templates"
        description={error}
        action={{ label: 'Try again', onClick: loadTemplates, variant: 'tertiary' }}
      />
    );
  }

  if (displayedTemplates.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={LayoutPanelLeft}
        title="No matching templates"
        description="Try adjusting your filters or search."
      />
    ) : (
      <EmptyState
        icon={LayoutPanelLeft}
        title="No templates available"
        description="Contact an administrator if you think this is an issue."
      />
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
        {displayedTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onClick={() => handleTemplateClick(template)}
            isSelected={selectedTemplateId === template.id}
            onPreview={onTemplatePreview ? () => onTemplatePreview(template) : undefined}
          />
        ))}
      </div>
      <Paginator
        pagination={pagination}
        onPageChange={setPage}
        className="mt-8"
      />
    </>
  );
};

export default TemplateGallery;
